import { nodeHasCapability } from '@/config/nodeTypes';
import type { NodeOperationState } from '@/hooks/data/useDomainDeployment';
import type {
  ImportPleskCertificateResponse,
  NodeStatusItem,
  SetupCDNNodeResponse,
} from '@/types/domain';
import type { Node } from '@/types/node';

export interface DomainNodeListProps {
  nodes: Node[];
  selectedNodeIds: string[];
  onToggle: (nodeId: string, checked: boolean) => void;
  disabled?: boolean;
  /** Show per-node deployment status and setup/import buttons (edit mode) */
  showDeployment: boolean;
  nodeStatuses: NodeStatusItem[];
  isLoadingStatuses: boolean;
  setupStates: Record<string, NodeOperationState<SetupCDNNodeResponse>>;
  importStates: Record<string, NodeOperationState<ImportPleskCertificateResponse>>;
  onSetup: (nodeId: string) => void;
  onImport: (nodeId: string) => void;
}

function StatusDot({ ok, label, okTitle, missingTitle }: {
  ok: boolean;
  label: string;
  okTitle: string;
  missingTitle: string;
}) {
  return (
    <span
      className={`domain-modal-status-dot ${ok ? 'domain-modal-status-ok' : 'domain-modal-status-missing'}`}
      title={ok ? okTitle : missingTitle}
    >
      {label}
    </span>
  );
}

/**
 * Node picker of the domain modal with per-node deployment status
 * (DNS / SSL / Nginx) and "Setup" / "Import Cert" actions.
 * Which indicators and actions a node gets depends on its type's capabilities.
 */
export function DomainNodeList({
  nodes,
  selectedNodeIds,
  onToggle,
  disabled = false,
  showDeployment,
  nodeStatuses,
  isLoadingStatuses,
  setupStates,
  importStates,
  onSetup,
  onImport,
}: DomainNodeListProps) {
  const completedSetups = Object.entries(setupStates).filter(([, state]) => state.result);

  return (
    <>
      <div className="domain-modal-node-list">
        {nodes.length === 0 ? (
          <span className="domain-modal-no-nodes">No nodes available</span>
        ) : (
          nodes.map((node) => {
            const status = nodeStatuses.find((s) => s.node_id === node.id);
            const setupState = setupStates[node.id];
            const importState = importStates[node.id];
            const isChecked = selectedNodeIds.includes(node.id);
            const hasNginx = nodeHasCapability(node, 'hasNginx');
            const importsCertificates = nodeHasCapability(node, 'importsCertificates');
            // Nodes with an IPv6 address also need an AAAA record
            const hasIpv6 = !!status?.ipv6_address;
            const needsSetup =
              !!status &&
              (!status.has_certificate ||
                !status.has_nginx_config ||
                !status.has_dns_record ||
                (hasIpv6 && !status.has_aaaa_record));
            const canAct = showDeployment && isChecked && !!status;

            return (
              <div key={node.id} className="domain-modal-node-row">
                <label className="domain-modal-node-checkbox">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => onToggle(node.id, e.target.checked)}
                    disabled={disabled}
                  />
                  <span className="domain-modal-node-info">
                    <span className="domain-modal-node-name">{node.name}</span>
                    <span className="domain-modal-node-ip">{node.ip_address}</span>
                    {node.ipv6_address && <span className="domain-modal-node-ip">{node.ipv6_address}</span>}
                  </span>
                  {showDeployment && status && !isLoadingStatuses && (
                    <span className="domain-modal-node-status">
                      {hasNginx && (
                        <StatusDot
                          ok={status.has_dns_record}
                          label="DNS"
                          okTitle="DNS record found"
                          missingTitle="DNS record missing"
                        />
                      )}
                      {hasNginx && hasIpv6 && (
                        <StatusDot
                          ok={status.has_aaaa_record}
                          label="AAAA"
                          okTitle={`AAAA record to ${status.ipv6_address} found`}
                          missingTitle={`AAAA record to ${status.ipv6_address} missing (IPv6 clients cannot use this node)`}
                        />
                      )}
                      <StatusDot
                        ok={status.has_certificate}
                        label="SSL"
                        okTitle="SSL certificate installed"
                        missingTitle="SSL certificate missing"
                      />
                      {hasNginx && (
                        <StatusDot
                          ok={status.has_nginx_config}
                          label="Nginx"
                          okTitle="Nginx config active"
                          missingTitle="Nginx config missing"
                        />
                      )}
                    </span>
                  )}
                </label>

                {/* Setup button for CDN nodes that need configuration */}
                {canAct && hasNginx && needsSetup && !setupState?.loading && (
                  <button
                    type="button"
                    className="domain-modal-setup-btn"
                    onClick={() => onSetup(node.id)}
                    title="Install certificate, configure nginx, add DNS A/AAAA records"
                  >
                    Setup
                  </button>
                )}
                {/* (Re-)import button for nodes that host the certificate */}
                {canAct && importsCertificates && !importState?.loading && (
                  <button
                    type="button"
                    className={status?.has_certificate ? 'domain-modal-reimport-btn' : 'domain-modal-import-btn'}
                    onClick={() => onImport(node.id)}
                    title={status?.has_certificate ? 'Re-import SSL certificate from Plesk' : 'Import SSL certificate from Plesk'}
                  >
                    {status?.has_certificate ? 'Re-import' : 'Import Cert'}
                  </button>
                )}

                {importState?.loading && <span className="domain-modal-setup-loading">Importing...</span>}
                {importState?.result && !importState.loading && (
                  <span
                    className={`domain-modal-setup-result ${importState.result.success ? 'domain-modal-setup-success' : 'domain-modal-setup-partial'}`}
                  >
                    {importState.result.success ? 'Imported' : importState.result.message}
                  </span>
                )}
                {importState?.error && !importState.loading && (
                  <span className="domain-modal-setup-error" title={importState.error}>
                    Failed
                  </span>
                )}

                {setupState?.loading && <span className="domain-modal-setup-loading">Setting up...</span>}
                {setupState?.result && !setupState.loading && (
                  <span
                    className={`domain-modal-setup-result ${setupState.result.success ? 'domain-modal-setup-success' : 'domain-modal-setup-partial'}`}
                  >
                    {setupState.result.success ? 'Done' : 'Partial'}
                  </span>
                )}
                {setupState?.error && !setupState.loading && (
                  <span className="domain-modal-setup-error" title={setupState.error}>
                    Failed
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
      <span className="domain-modal-helper">
        Select CDN nodes to serve this domain (optional, can select multiple)
      </span>

      {/* Setup results summary */}
      {completedSetups.length > 0 && (
        <div className="domain-modal-setup-summary">
          {completedSetups.map(([nodeId, { result }]) => {
            const node = nodes.find((n) => n.id === nodeId);
            return (
              <div key={nodeId} className="domain-modal-setup-summary-item">
                <strong>{node?.name || nodeId}:</strong>
                {result?.certificate_installed ? ' SSL' : ''}
                {result?.certificate_error && (
                  <span className="domain-modal-setup-error-text"> (SSL: {result.certificate_error})</span>
                )}
                {result?.nginx_configured && !result.nginx_skipped ? ' Nginx' : ''}
                {result?.nginx_error && (
                  <span className="domain-modal-setup-error-text"> (Nginx: {result.nginx_error})</span>
                )}
                {result?.dns_record_added && !result.dns_skipped ? ' DNS' : ''}
                {result?.dns_error && (
                  <span className="domain-modal-setup-error-text"> (DNS: {result.dns_error})</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
